import * as React from "react"
import { Head, router } from "@inertiajs/react"
import { Copy, KeyRound, Plus } from "lucide-react"
import { Button } from "@ui/Button"
import { Card } from "@ui/Card"
import { Dialog } from "@ui/Dialog"
import { EmptyState } from "@ui/EmptyState"
import { Field } from "@ui/Field"
import { Input } from "@ui/Input"
import { toast } from "sonner"

interface ApiKeyRow {
  id: number
  name: string
  last_used_at: string | null
  revoked_at: string | null
  created_at: string
}

export default function ApiKeys({
  api_keys,
  new_token,
}: {
  api_keys: ApiKeyRow[]
  new_token: string | null
}) {
  const [dialogOpen, setDialogOpen] = React.useState(false)
  const active = api_keys.filter((k) => !k.revoked_at)

  return (
    <>
      <Head title="API keys · Fetch" />
      <div className="mx-auto max-w-3xl px-6 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-title3 font-semibold text-cloverhound-gray-900 dark:text-cloverhound-gray-50">
              API keys
            </h1>
            <p className="mt-1 text-small text-cloverhound-gray-600">
              Drive Fetch from a coding agent or CLI. See the{" "}
              <a href="/api-docs.html" className="text-cloverhound-indigo-600 hover:underline dark:text-cloverhound-indigo-300">
                API reference
              </a>
              .
            </p>
          </div>
          <Button onClick={() => setDialogOpen(true)}>
            <Plus className="size-4" aria-hidden="true" />
            New key
          </Button>
        </div>

        {new_token && <NewTokenBanner token={new_token} />}

        {active.length === 0 ? (
          <EmptyState
            icon={<KeyRound className="size-6" aria-hidden="true" />}
            title="No API keys"
            description="Create a key to authenticate requests to /api/v1."
          />
        ) : (
          <div className="flex flex-col gap-2">
            {active.map((key) => (
              <Card key={key.id} padding="md">
                <div className="flex items-center gap-3">
                  <KeyRound className="size-4 text-cloverhound-gray-500" aria-hidden="true" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-small font-medium text-cloverhound-gray-900 dark:text-cloverhound-gray-50">
                      {key.name}
                    </p>
                    <p className="text-micro text-cloverhound-gray-500">
                      Created {new Date(key.created_at).toLocaleDateString()}
                      {key.last_used_at ? ` · last used ${new Date(key.last_used_at).toLocaleDateString()}` : " · never used"}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (!confirm(`Revoke "${key.name}"? Requests using it will start failing.`)) return
                      router.delete(`/settings/api_keys/${key.id}`, { preserveScroll: true })
                    }}
                    className="text-cloverhound-red-500"
                  >
                    Revoke
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <NewKeyDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </>
  )
}

function NewTokenBanner({ token }: { token: string }) {
  return (
    <Card padding="md" className="mb-4 border-cloverhound-green-500/40 bg-cloverhound-green-50 dark:bg-cloverhound-green-500/10">
      <p className="mb-2 text-small font-medium text-cloverhound-green-700 dark:text-cloverhound-green-400">
        Copy your new key now — it won't be shown again.
      </p>
      <div className="flex items-center gap-2">
        <code className="min-w-0 flex-1 truncate rounded-md bg-white px-3 py-2 font-mono text-mini text-cloverhound-gray-900 dark:bg-cloverhound-gray-950 dark:text-cloverhound-gray-50">
          {token}
        </code>
        <Button
          size="sm"
          onClick={() => {
            navigator.clipboard.writeText(token)
            toast.success("Copied")
          }}
        >
          <Copy className="size-3.5" aria-hidden="true" />
          Copy
        </Button>
      </div>
    </Card>
  )
}

function NewKeyDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [name, setName] = React.useState("")

  function submit(e: React.FormEvent) {
    e.preventDefault()
    router.post("/settings/api_keys", { name }, { onFinish: () => (setName(""), onClose()) })
  }

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Content size="sm">
        <form onSubmit={submit}>
          <Dialog.Header>
            <Dialog.Title>New API key</Dialog.Title>
          </Dialog.Header>
          <Dialog.Body>
            <Field label="Name" description="A label to remember what this key is for.">
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Coding agent" autoFocus />
            </Field>
          </Dialog.Body>
          <Dialog.Footer>
            <Button variant="secondary" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">Create key</Button>
          </Dialog.Footer>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  )
}
